import { useEffect } from "react";
import { createRoute, useNavigate, useSearch } from "@tanstack/react-router";

import { rootRoute } from "./root.tsx";
import { Spinner } from "../components/ui.tsx";
import { rememberOrg } from "../lib/identity.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  /merchant → 「我的」的商家身份
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **商家端不再是一个单独的页面。**
 *    以前这一页把邀请、进货、定价、订单、券、收益、提现、等级、加价率、
 *    专属链接**全堆在一屏里** —— 划到底要好几屏，而装修师傅是在工地上
 *    单手划手机。找东西比做事还费劲。
 *
 *    现在改成：在「我的」顶部**切换身份**，切到某家店就整页换成经营内容，
 *    每个入口通向一个**只做一件事**的页面。
 *
 * ⚠️ 这个路由保留下来做**重定向**：外面（旧书签、别的页面、二维码）
 *    还有指向 `/merchant` 的链接，直接删掉会变成 404。
 */
function MerchantRedirect() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/merchant" }) as { orgId?: number };

  useEffect(() => {
    /**
     * ⚠️ 带了 orgId 就先把身份切过去 —— 否则从「我的定价」这类页面
     *    按返回回到这里，会落到买家视角，等于把人踢出了经营流程。
     */
    rememberOrg(search.orgId);
    /** ⚠️ `replace` —— 不留历史，否则按返回会弹回来又被重定向走，出不去。 */
    navigate({ to: "/me", replace: true });
  }, [navigate, search.orgId]);

  /*
    ⚠️ 这是个纯重定向页，别套 `TabPage` —— 那是手机版的外壳（含底部标签栏），
       在电脑上会闪一下 480 宽的手机界面再跳走。
  */
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <Spinner label="正在打开商家端" compact />
    </div>
  );
}

export const merchantRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/merchant",
  component: MerchantRedirect,
  validateSearch: (s: Record<string, unknown>): { orgId?: number } => ({
    orgId: s.orgId ? Number(s.orgId) : undefined,
  }),
});
